import React, { useState, useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { X, Download, Smartphone } from 'lucide-react';
import { Button } from './button';
import { motion, AnimatePresence } from 'framer-motion';
import { useHospitalBranding } from '../../contexts/HospitalBrandingContext';
import { API_BASE_URL } from '../../config/api';

// Helper function to detect iOS
const isIOS = () => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || 
         (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
};

// Helper function to detect Android
const isAndroid = () => {
  return /Android/.test(navigator.userAgent);
};

// Helper function to detect mobile device
const isMobile = () => {
  return isIOS() || isAndroid() || /Mobile|Tablet/.test(navigator.userAgent);
};

// Helper function to detect browser type
const getBrowser = () => {
  const ua = navigator.userAgent.toLowerCase();
  
  if (ua.indexOf('edg') > -1) return 'edge';
  if (ua.indexOf('chrome') > -1 && ua.indexOf('edg') === -1) return 'chrome';
  if (ua.indexOf('safari') > -1 && ua.indexOf('chrome') === -1) return 'safari';
  if (ua.indexOf('firefox') > -1) return 'firefox';
  if (ua.indexOf('samsung') > -1) return 'samsung';
  if (ua.indexOf('opera') > -1 || ua.indexOf('opr') > -1) return 'opera';
  
  return 'unknown';
};

// Helper function to check if browser supports beforeinstallprompt
const supportsInstallPrompt = () => {
  const browser = getBrowser();
  // Chrome, Edge, Samsung Internet, Opera support beforeinstallprompt
  return ['chrome', 'edge', 'samsung', 'opera'].includes(browser);
};

// Helper function to detect if device is standalone (already installed)
const isStandalone = () => {
  return window.matchMedia('(display-mode: standalone)').matches || 
         window.navigator.standalone === true ||
         (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches);
};

const PWAInstallPrompt = () => {
  const location = useLocation();
  const { hospitalSlug } = useParams();
  const { branding } = useHospitalBranding();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isHospitalPage, setIsHospitalPage] = useState(false);
  const [deviceInfo, setDeviceInfo] = useState({
    isIOS: false,
    isAndroid: false,
    isMobile: false,
    browser: 'unknown',
    supportsAutoInstall: false
  });
  const [showManualInstructions, setShowManualInstructions] = useState(false);

  // Check if we're on a hospital page (not main landing page)
  useEffect(() => {
    const pathname = location.pathname;
    
    // Exclude main landing page and other non-hospital routes
    const excludedPaths = ['/', '/login', '/register', '/pricing', '/admin/hub', '/patient', '/doctor'];
    
    // Check if pathname starts with excluded paths
    const isExcluded = excludedPaths.some(path => {
      if (pathname === path) return true;
      if (pathname.startsWith(path + '/')) return true;
      return false;
    });
    
    // Extract potential hospital slug from pathname
    // Pattern: /slug or /slug/anything
    const pathParts = pathname.split('/').filter(Boolean);
    const firstPart = pathParts[0];
    
    // Known non-slug paths that should be excluded
    const knownNonSlugs = ['admin', 'doctor', 'patient', 'login', 'register', 'pricing', 'api'];
    
    // Check if we have a hospital slug in the URL
    // Either from useParams or extracted from pathname
    const detectedSlug = hospitalSlug || (firstPart && !knownNonSlugs.includes(firstPart.toLowerCase()) ? firstPart : null);
    const hasHospitalSlug = detectedSlug && !isExcluded;
    
    // Only show PWA on hospital pages (routes with :hospitalSlug)
    setIsHospitalPage(hasHospitalSlug);
    
    // Debug logging
    console.log('PWA Install Prompt Debug:', {
      pathname,
      hospitalSlug,
      detectedSlug,
      hasHospitalSlug,
      isExcluded,
      isHospitalPage: hasHospitalSlug
    });
  }, [location.pathname, hospitalSlug]);

  // Update manifest dynamically when hospital branding is available
  useEffect(() => {
    if (isHospitalPage) {
      // Extract slug from pathname if not available from useParams
      const pathParts = location.pathname.split('/').filter(Boolean);
      const detectedSlug = hospitalSlug || pathParts[0];
      
      if (detectedSlug) {
        const manifestLink = document.querySelector('link[rel="manifest"]');
        if (manifestLink) {
          // Update manifest link to point to dynamic manifest API
          const manifestUrl = `${API_BASE_URL}/get_manifest.php?slug=${encodeURIComponent(detectedSlug)}`;
          manifestLink.setAttribute('href', manifestUrl);
          console.log('PWA Install Prompt: Updated manifest URL to', manifestUrl);
          
          // Update meta tags for better PWA support
          const manifestName = branding?.name 
            ? `Dental Hub - ${branding.name}` 
            : `Dental Hub - ${detectedSlug}`;
          
          // Update apple-mobile-web-app-title
          let appleTitle = document.querySelector('meta[name="apple-mobile-web-app-title"]');
          if (appleTitle) {
            appleTitle.setAttribute('content', manifestName);
          } else {
            appleTitle = document.createElement('meta');
            appleTitle.name = 'apple-mobile-web-app-title';
            appleTitle.content = manifestName;
            document.head.appendChild(appleTitle);
          }
          
          // Update application-name meta tag
          let appName = document.querySelector('meta[name="application-name"]');
          if (appName) {
            appName.setAttribute('content', manifestName);
          } else {
            appName = document.createElement('meta');
            appName.name = 'application-name';
            appName.content = manifestName;
            document.head.appendChild(appName);
          }
        }
      }
    } else {
      // Reset to default manifest for main landing page
      const manifestLink = document.querySelector('link[rel="manifest"]');
      if (manifestLink) {
        manifestLink.setAttribute('href', '/manifest.json');
      }
    }
  }, [isHospitalPage, branding, hospitalSlug, location.pathname]);

  // Detect device and browser type
  useEffect(() => {
    const browser = getBrowser();
    const supportsAuto = supportsInstallPrompt();
    
    setDeviceInfo({
      isIOS: isIOS(),
      isAndroid: isAndroid(),
      isMobile: isMobile(),
      browser: browser,
      supportsAutoInstall: supportsAuto
    });
    
    console.log('PWA Install Prompt: Device Info', {
      isIOS: isIOS(),
      isAndroid: isAndroid(),
      isMobile: isMobile(),
      browser: browser,
      supportsAutoInstall: supportsAuto
    });
  }, []);

  useEffect(() => {
    // Only show PWA prompt on hospital pages
    if (!isHospitalPage) {
      console.log('PWA Install Prompt: Not on hospital page, skipping');
      return;
    }

    console.log('PWA Install Prompt: Setting up for hospital page');

    // Check if app is already installed
    if (isStandalone()) {
      console.log('PWA Install Prompt: App already installed (standalone mode)');
      setIsInstalled(true);
      return;
    }

    // Detect device and browser info directly (don't rely on state)
    const currentIsIOS = isIOS();
    const currentBrowser = getBrowser();
    const currentSupportsAuto = supportsInstallPrompt();
    
    console.log('PWA Install Prompt: Current device info', {
      isIOS: currentIsIOS,
      browser: currentBrowser,
      supportsAuto: currentSupportsAuto
    });

    // For iOS devices OR browsers that don't support beforeinstallprompt, show manual instructions
    // iOS Safari, Chrome on iOS, Firefox on iOS all need manual instructions
    if (currentIsIOS || !currentSupportsAuto) {
      console.log('PWA Install Prompt: iOS device or browser does not support auto-install, showing manual instructions');
      // Check if user dismissed the prompt before
      const dismissed = localStorage.getItem('pwa-install-dismissed');
      if (dismissed) {
        const dismissedTime = parseInt(dismissed, 10);
        const daysSinceDismissed = (Date.now() - dismissedTime) / (1000 * 60 * 60 * 24);
        // Show again after 7 days
        if (daysSinceDismissed < 7) {
          console.log('PWA Install Prompt: User dismissed within 7 days, not showing');
          return;
        }
      }
      // Show manual instructions after a delay
      // For iOS, show immediately; for others, wait 2 seconds
      const delay = currentIsIOS ? 1000 : 2000;
      setTimeout(() => {
        setShowManualInstructions(true);
        setShowPrompt(true);
        console.log('PWA Install Prompt: Showing manual instructions', {
          isIOS: currentIsIOS,
          browser: currentBrowser
        });
      }, delay);
      return;
    }

    // For Chrome/Edge, listen for beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      console.log('PWA Install Prompt: beforeinstallprompt event received');
      // Prevent the default browser install prompt
      e.preventDefault();
      // Store the event for later use
      setDeferredPrompt(e);
      // Show our custom install prompt
      setShowPrompt(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    console.log('PWA Install Prompt: Added beforeinstallprompt listener');

    // Check if user dismissed the prompt before
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    if (dismissed) {
      const dismissedTime = parseInt(dismissed, 10);
      const daysSinceDismissed = (Date.now() - dismissedTime) / (1000 * 60 * 60 * 24);
      // Show again after 7 days
      if (daysSinceDismissed < 7) {
        console.log('PWA Install Prompt: User dismissed within 7 days, not showing');
        setShowPrompt(false);
      } else {
        console.log('PWA Install Prompt: 7 days passed since dismissal, will show if event fires');
      }
    }

    // Also check if manifest is valid (this helps trigger the install prompt)
    const checkManifest = async () => {
      try {
        const manifestLink = document.querySelector('link[rel="manifest"]');
        if (manifestLink && manifestLink.href) {
          const response = await fetch(manifestLink.href);
          if (response.ok) {
            const manifest = await response.json();
            console.log('PWA Install Prompt: Manifest loaded successfully', manifest);
          } else {
            console.warn('PWA Install Prompt: Manifest failed to load', response.status);
          }
        }
      } catch (error) {
        console.error('PWA Install Prompt: Error checking manifest', error);
      }
    };
    
    // Check manifest after a short delay to ensure it's updated
    setTimeout(checkManifest, 1000);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      console.log('PWA Install Prompt: Removed beforeinstallprompt listener');
    };
  }, [isHospitalPage, deviceInfo]);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      return;
    }

    // Show the install prompt
    deferredPrompt.prompt();

    // Wait for the user to respond
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      console.log('User accepted the install prompt');
      setIsInstalled(true);
    } else {
      console.log('User dismissed the install prompt');
      // Store dismissal time
      localStorage.setItem('pwa-install-dismissed', Date.now().toString());
    }

    // Clear the deferred prompt
    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    // Store dismissal time
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
  };

  // Don't show if not on hospital page or already installed
  if (!isHospitalPage || isInstalled || !showPrompt) {
    return null;
  }

  // For iOS devices or browsers that don't support auto-install, show manual instructions
  if (showManualInstructions) {
    const getInstructions = () => {
      // Use deviceInfo state and helper functions directly
      const isIOSDevice = deviceInfo.isIOS || isIOS();
      const isAndroidDevice = deviceInfo.isAndroid || isAndroid();
      const browser = deviceInfo.browser || getBrowser();
      
      if (isIOSDevice) {
        return {
          title: '📱 On iPhone/iPad:',
          steps: [
            'Tap the Share button (□↑) at the bottom of the screen',
            'Scroll down and tap "Add to Home Screen"',
            'Tap "Add" in the top right corner'
          ]
        };
      } else if (isAndroidDevice) {
        if (browser === 'chrome' || browser === 'samsung') {
          return {
            title: '📱 On Android:',
            steps: [
              'Tap the menu (⋮) in the top right corner',
              'Tap "Add to Home screen" or "Install app"',
              'Tap "Add" or "Install" to confirm'
            ]
          };
        } else {
          return {
            title: '📱 On Android:',
            steps: [
              'Tap the menu (⋮) in the top right corner',
              'Look for "Add to Home screen" or "Install" option',
              'Follow the on-screen instructions'
            ]
          };
        }
      } else if (browser === 'safari') {
        return {
          title: '💻 On Mac Safari:',
          steps: [
            'Click File → Add to Dock',
            'Or click the Share button → Add to Home Screen',
            'The app will appear in your Dock'
          ]
        };
      } else if (browser === 'firefox') {
        return {
          title: '🌐 On Firefox:',
          steps: [
            'Click the menu (☰) in the top right',
            'Look for "Install" or "Add to Home Screen"',
            'Follow the browser-specific instructions'
          ]
        };
      } else {
        return {
          title: '📱 Add to Home Screen:',
          steps: [
            'Look for the install icon in your browser\'s address bar',
            'Or check your browser menu for "Add to Home Screen" or "Install"',
            'Follow your browser\'s specific instructions'
          ]
        };
      }
    };

    const instructions = getInstructions();

    return (
      <AnimatePresence>
        {showPrompt && (
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 100 }}
            className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50 w-full max-w-md px-4"
          >
            <div className="bg-gray-800 border border-gray-700 rounded-lg shadow-2xl p-4">
              <div className="flex items-start gap-4 mb-3">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center">
                    <Smartphone className="h-6 w-6 text-white" />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-white font-semibold mb-1">
                    Install {branding?.name ? `Dental Hub - ${branding.name}` : 'Dental Hub'}
                  </h3>
                  <p className="text-gray-300 text-sm mb-3">
                    Add this app to your home screen for quick access
                  </p>
                </div>
                <Button
                  onClick={handleDismiss}
                  size="sm"
                  variant="ghost"
                  className="text-gray-400 hover:text-white flex-shrink-0"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              
              <div className="bg-gray-900 rounded-lg p-3 mb-3">
                <p className="text-white text-sm font-medium mb-2">{instructions.title}</p>
                <ol className="text-gray-300 text-xs space-y-1.5 list-decimal list-inside">
                  {instructions.steps.map((step, index) => (
                    <li key={index}>{step}</li>
                  ))}
                </ol>
              </div>
              
              <Button
                onClick={handleDismiss}
                size="sm"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              >
                Got it!
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    );
  }

  // For Chrome/Edge, show install button (requires deferredPrompt)
  if (!deferredPrompt) {
    return null;
  }

  return (
    <AnimatePresence>
      {showPrompt && (
        <motion.div
          initial={{ opacity: 0, y: 100 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 100 }}
          className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50 w-full max-w-md px-4"
        >
          <div className="bg-gray-800 border border-gray-700 rounded-lg shadow-2xl p-4 flex items-center gap-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center">
                <Smartphone className="h-6 w-6 text-white" />
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-white font-semibold mb-1">
                Install {branding?.name ? `Dental Hub - ${branding.name}` : 'Dental Hub'}
              </h3>
              <p className="text-gray-300 text-sm">
                Install our app for a better experience and quick access
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={handleInstallClick}
                size="sm"
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                <Download className="h-4 w-4 mr-1" />
                Install
              </Button>
              <Button
                onClick={handleDismiss}
                size="sm"
                variant="ghost"
                className="text-gray-400 hover:text-white"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PWAInstallPrompt;
